<template>
  <div
    v-if="willModalOpen"
    :class="[
      bemBlockMod({
        [modalModifier]: modalModifier,
        open: isModalOpen,
      }),
    ]"
    role="dialog"
    data-allow-multiple-modals="true"
    tabindex="-1"
  >
    <div
      :class="bemElement('dialog', 'scrollable')"
      role="document"
    >
      <div :class="bemElement('content')">
        <div :class="bemElement('header')">
          <template v-if="hasBackButton || hasMiniCart">
            <!-- simple web fake header -->
            <button
              v-if="hasBackButton"
              type="button"
              :class="[bemElement('btn-back'), 'btn', 'btn--back']"
              @click.stop="close"
            >
              {{ $msg('header.back', 'header') }}
            </button>
            <BaseMinicart
              v-if="hasMiniCart"
            />
          </template>
          <template v-else>
            <!-- modal default header -->
            <button
              v-if="hasCloseButton"
              :class="[bemElement('close'), 'btn-close', 'modal-close-action']"
              @click.stop="close"
            >
              <span class="visually-hidden">{{ $msg('global.close', 'locale') }}</span>
            </button>
          </template>
        </div>
        <div
          ref="body"
          :class="bemElement('body')"
          @scrollable-elements:update="setScrollLocks"
        >
          <slot />
        </div>
        <!-- <div :class="bemElement('footer')" /> -->
      </div>
    </div>
    <div
      v-if="showBackdrop"
      :class="bemElement('backdrop', {
        'interaction': closeOnBackdropInteraction,
      })"
      @click="handleBackdropInteraction"
      @touchstart="handleBackdropInteraction"
    />
  </div>
</template>

<script>
import { lock, clearBodyLocks } from 'tua-body-scroll-lock';
import bemClassesMixin from '../mixins/bemClassesMixin';
import { types } from '../store';
import BaseMinicart from './BaseMinicart.vue';
import { isIOSUserAgent } from '../../common/utils/device';

export default {
  name: 'BaseModal',

  components: {
    BaseMinicart,
  },

  mixins: [
    bemClassesMixin,
  ],

  props: {
    hasBackButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasCloseButton: {
      type: Boolean,
      required: false,
      default: true,
    },
    hasMiniCart: {
      type: Boolean,
      required: false,
      default: false,
    },
    isOpen: {
      type: Boolean,
      required: true,
      default: false,
    },
    modalModifier: {
      type: String,
      required: false,
      default: '',
    },
    showBackdrop: {
      type: Boolean,
      required: false,
      default: false,
    },
    closeOnBackdropInteraction: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      isModalOpen: false,
      willModalOpen: false,
      scrollableElements: [],
    };
  },

  watch: {
    isOpen() {
      this.updateModalState();
    },
  },

  mounted() {
    this.updateModalState();
  },

  methods: {
    updateModalState() {
      if (this.isModalOpen !== this.isOpen) {
        if (this.isOpen) {
          this.open();
        } else {
          this.close();
        }
      }
    },
    close() {
      document.body.classList.remove('base-modal-open');
      this.headerEvent();
      if (isIOSUserAgent()) {
        // Ensures all body locks are removed, due to lock posibly being called multiple times
        clearBodyLocks();
      }
      this.willModalOpen = true;
      this.isModalOpen = false;

      setTimeout(() => {
        this.willModalOpen = false;
        this.$emit('closed');
      }, 300);

      if (this.modalModifier === 'fullscreen') {
        // Apply this behind the modal, before closing animation
        // It will hide unnecessary layout changes to the user
        this.$store.commit(types.header.handleFullscreenModalClosing);
      }
    },
    open() {
      this.willModalOpen = true;
      this.$nextTick(() => {
        this.$refs.body.scrollTop = 0;
      });

      setTimeout(() => {
        this.isModalOpen = true;
        if (this.showBackdrop) {
          document.body.classList.add('base-modal-open');
          this.headerEvent();
        }
        this.setScrollLocks();
        this.$emit('open');
      }, 50);

      setTimeout(() => {
        if (this.modalModifier === 'fullscreen') {
          // Apply this behind the modal, after opening animation
          // It will hide unnecessary layout changes to the user
          this.$store.commit(types.header.handleFullscreenModalOpening);
        }
      }, 300 + 50); // css animation duration (t3 = 300ms) plus before delay (50ms)
    },
    // Triggered on modal open and whenever the scrollable-elements:update event is fired from a child component
    setScrollLocks() {
      if (isIOSUserAgent()) {
        this.scrollableElements = Array.from(this.$refs.body.querySelectorAll('.overflow'));
        this.scrollableElements.push(this.$refs.body);
        lock(this.scrollableElements);
      }
    },
    headerEvent() {
      const event = new CustomEvent('header:calculateCustomProperties', {
        detail: {
          origin: 'base-modal',
          data: {
            active: this.isModalOpen,
          },
        },
      });
      document.dispatchEvent(event);
    },
    handleBackdropInteraction(event) {
      this.$emit('backdrop-interaction', event);
      if (this.closeOnBackdropInteraction) this.close();
    },
  },
};
</script>
